import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TrainerLayout from './TrainerLayout';
import { TrainerContext } from '../../context/TrainerContext';
import { useTrainerDashboard } from './useTrainerDashboard';
import Widget from '../commons/widgets/Widget';
import WidgetGrid from '../commons/widgets/WidgetGrid';
import {
    FaDog,
    FaUsers,
    FaUserTie,
    FaLayerGroup,
    FaCalendarAlt,
    FaClock,
    FaChartBar,
    FaBell,
    FaPlus,
    FaCheckCircle,
    FaHourglassHalf
} from 'react-icons/fa';
import { PiDog } from 'react-icons/pi';
import SetupChecklist from './SetupChecklist';
import '../../styles/TrainerDashboard.css';

const TrainerDashboard = () => {
    const { t, i18n } = useTranslation('dashboard');
    const { trainer } = useContext(TrainerContext);
    const { dashboardData, loading, error, refresh } = useTrainerDashboard();
    const navigate = useNavigate();

    const currentLocale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLocale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString(currentLocale, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeRange = (startTime, endTime) => {
        return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    };

    // Calculate max for the mini chart
    const getMaxDailyCount = () => {
        if (!dashboardData?.weeklyOverview?.dailyBreakdown) return 5;
        const max = Math.max(...dashboardData.weeklyOverview.dailyBreakdown.map(d => d.count));
        return Math.max(max, 1);
    };

    if (loading) {
        return (
            <TrainerLayout>
                <div className="trainer-dashboard-container">
                    <div className="dashboard-header">
                        <div className="header-left">
                            <h1>{t('trainer.welcome', { name: trainer?.firstName || t('trainer.defaultName') })}</h1>
                            <p>{t('trainer.loadingDashboard')}</p>
                        </div>
                    </div>
                    <WidgetGrid columns="3" gap="medium">
                        <Widget loading={true} title={t('trainer.schoolStats')} />
                        <Widget loading={true} title={t('trainer.nextFreeSlot')} />
                        <Widget loading={true} title={t('trainer.pendingRequests')} />
                        <WidgetGrid.Item span={2}>
                            <Widget loading={true} title={t('trainer.todaySchedule')} />
                        </WidgetGrid.Item>
                        <Widget loading={true} title={t('trainer.quickActions')} />
                        <WidgetGrid.Item span={3}>
                            <Widget loading={true} title={t('trainer.weeklyOverview')} />
                        </WidgetGrid.Item>
                    </WidgetGrid>
                </div>
            </TrainerLayout>
        );
    }

    if (error) {
        return (
            <TrainerLayout>
                <div className="trainer-dashboard-container">
                    <div className="dashboard-header">
                        <div className="header-left">
                            <h1>{t('trainer.welcome', { name: trainer?.firstName || t('trainer.defaultName') })}</h1>
                            <p>{t('trainer.loadError')}</p>
                        </div>
                    </div>
                    <Widget
                        type="info"
                        title={t('trainer.errorTitle')}
                        error={error}
                        onRetry={refresh}
                    />
                </div>
            </TrainerLayout>
        );
    }

    const { schoolStats, todaySchedule, pendingRequests, nextFreeSlot, weeklyOverview } = dashboardData || {};

    return (
        <TrainerLayout>
            <div className="trainer-dashboard-container">
                {/* Header */}
                <div className="dashboard-header">
                    <div className="header-left">
                        <h1>{t('trainer.welcome', { name: trainer?.firstName || t('trainer.defaultName') })}</h1>
                        <p>
                            {schoolStats?.schoolName
                                ? t('trainer.schoolLabel', { name: schoolStats.schoolName })
                                : t('trainer.yourDashboard')}
                        </p>
                    </div>
                    <div className="header-right">
                        <button
                            className="refresh-button"
                            onClick={refresh}
                            title={t('trainer.refreshData')}
                        >
                            <FaClock /> {t('trainer.refresh')}
                        </button>
                    </div>
                </div>

                {/* Setup Checklist - shown until all setup steps are complete */}
                <SetupChecklist />

                {/* Widgets Grid */}
                <WidgetGrid columns="3" gap="medium">

                    {/* School Statistics Widget */}
                    <Widget
                        type="stat"
                        title={t('trainer.schoolStats')}
                        subtitle={schoolStats?.schoolName || t('trainer.yourSchool')}
                    >
                        <div className="school-stats-actions">
                            <div className="stat-action-box">
                                <FaDog className="stat-action-icon" />
                                <span className="stat-action-label">{schoolStats?.totalDogs || 0} {t('trainer.dogsCount')}</span>
                            </div>
                            <div className="stat-action-box">
                                <FaUsers className="stat-action-icon" />
                                <span className="stat-action-label">{schoolStats?.totalOwners || 0} {t('trainer.ownersCount')}</span>
                            </div>
                            <div className="stat-action-box">
                                <FaUserTie className="stat-action-icon" />
                                <span className="stat-action-label">{schoolStats?.totalTrainers || 0} {t('trainer.trainersCount')}</span>
                            </div>
                        </div>
                    </Widget>

                    {/* Next Free Slot Widget */}
                    <Widget
                        type="slots"
                        title={t('trainer.nextFreeSlot')}
                    >
                        {nextFreeSlot?.slotTime ? (
                            <div className="next-slot-content">
                                <div className="slot-date">
                                    <div className="slot-info">
                                        <span className="slot-day">{t(`trainer.dayNames.${nextFreeSlot.dayOfWeek}`, { defaultValue: nextFreeSlot.dayOfWeek })}</span>
                                        <span className="slot-time">{formatTime(nextFreeSlot.slotTime)}</span>
                                    </div>
                                </div>
                                {nextFreeSlot.hasSlotToday && (
                                    <Widget.Badge variant="success">{t('trainer.today')}</Widget.Badge>
                                )}
                                <Widget.Button
                                    variant="primary"
                                    onClick={() => navigate('/calendar')}
                                    icon={<FaPlus />}
                                >
                                    {t('trainer.addTraining')}
                                </Widget.Button>
                            </div>
                        ) : (
                            <Widget.EmptyState
                                message={nextFreeSlot?.message || t('trainer.noFreeSlots')}
                                icon={<FaClock />}
                            />
                        )}
                    </Widget>

                    {/* Pending Requests Widget */}
                    <Widget
                        type="info"
                        title={t('trainer.pendingRequests')}
                        headerAction={
                            pendingRequests?.length > 0 && (
                                <Widget.Badge variant="warning">
                                    {pendingRequests.length}
                                </Widget.Badge>
                            )
                        }
                    >
                        {pendingRequests?.length > 0 ? (
                            <div className="pending-requests-list">
                                {pendingRequests.slice(0, 4).map((request, index) => (
                                    <Widget.ListItem
                                        key={request.trainingId || index}
                                        primary={request.dogName}
                                        secondary={`${request.ownerName} • ${formatDate(request.requestedTime)}`}
                                        icon={<FaBell />}
                                        status="warning"
                                        onClick={() => navigate('/calendar')}
                                    />
                                ))}
                                {pendingRequests.length > 4 && (
                                    <div className="more-requests">
                                        +{pendingRequests.length - 4} {t('trainer.moreRequests')}
                                    </div>
                                )}
                                <Widget.Button
                                    variant="primary"
                                    fullWidth
                                    onClick={() => navigate('/calendar')}
                                >
                                    {t('trainer.viewCalendar')}
                                </Widget.Button>
                            </div>
                        ) : (
                            <Widget.EmptyState
                                message={t('trainer.noPendingRequests')}
                                icon={<FaCheckCircle />}
                            />
                        )}
                    </Widget>

                    {/* Today's Schedule Widget - Now spans 2 columns */}
                    <WidgetGrid.Item span={2}>
                        <Widget
                            type="schedule"
                            title={t('trainer.todaySchedule')}
                            subtitle={t('trainer.trainingsCount', { count: todaySchedule?.totalTrainingsToday || 0 })}
                            headerAction={
                                <Widget.Badge
                                    variant={todaySchedule?.upcomingTrainingsToday > 0 ? 'primary' : 'success'}
                                >
                                    {todaySchedule?.completedTrainingsToday || 0}/{todaySchedule?.totalTrainingsToday || 0}
                                </Widget.Badge>
                            }
                        >
                            {todaySchedule?.trainings?.length > 0 ? (
                                <div className="today-trainings-list">
                                    {todaySchedule.trainings.slice(0, 6).map((training, index) => (
                                        <Widget.ScheduleItem
                                            key={training.trainingId || index}
                                            name={training.dogName}
                                            time={formatTimeRange(training.startTime, training.endTime)}
                                            label={training.type === 'group'
                                                ? `${t('trainer.groupLabel')}: ${training.groupName || training.serviceName}`
                                                : training.serviceName || t('trainer.individual')}
                                            status={training.isCompleted ? 'confirmed' : 'default'}
                                            onClick={() => navigate('/calendar')}
                                        />
                                    ))}
                                    {todaySchedule.trainings.length > 6 && (
                                        <div className="more-trainings">
                                            +{todaySchedule.trainings.length - 6} {t('trainer.more')}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Widget.EmptyState
                                    message={t('trainer.noTrainingsToday')}
                                    icon={<FaCalendarAlt />}
                                    action={
                                        <Widget.Button
                                            variant="primary"
                                            onClick={() => navigate('/calendar')}
                                        >
                                            {t('trainer.openCalendar')}
                                        </Widget.Button>
                                    }
                                />
                            )}
                        </Widget>
                    </WidgetGrid.Item>

                    {/* Quick Actions Widget */}
                    <Widget type="default" title={t('trainer.quickActions')}>
                        <Widget.QuickActions
                            actions={[
                                {
                                    icon: <FaCalendarAlt />,
                                    label: t('trainer.calendarAction'),
                                    onClick: () => navigate('/calendar'),
                                    variant: 'primary'
                                },
                                {
                                    icon: <FaDog />,
                                    label: t('trainer.dogsAction'),
                                    onClick: () => navigate('/podopieczni'),
                                    variant: 'secondary'
                                },
                                {
                                    icon: <FaUsers />,
                                    label: t('trainer.ownersAction'),
                                    onClick: () => navigate('/owners/all'),
                                    variant: 'secondary'
                                }
                            ]}
                        />
                    </Widget>

                    {/* Weekly Overview Widget - Spans full width */}
                    <WidgetGrid.Item span={3}>
                        <Widget
                            type="stat"
                            title={t('trainer.weeklyOverview')}
                            subtitle={t('trainer.yourStats')}
                        >
                            <div className="weekly-stats-row">
                                <div className="weekly-stat">
                                    <PiDog className="weekly-stat-icon trainings" />
                                    <span className="weekly-stat-value">{weeklyOverview?.totalTrainingsThisWeek || 0}</span>
                                    <span className="weekly-stat-label">{t('trainer.weekTotal')}</span>
                                </div>
                                <div className="weekly-stat">
                                    <FaCheckCircle className="weekly-stat-icon success" />
                                    <span className="weekly-stat-value">{weeklyOverview?.completedTrainingsThisWeek || 0}</span>
                                    <span className="weekly-stat-label">{t('trainer.weekCompleted')}</span>
                                </div>
                                <div className="weekly-stat">
                                    <FaHourglassHalf className="weekly-stat-icon pending" />
                                    <span className="weekly-stat-value">{weeklyOverview?.upcomingTrainingsThisWeek || 0}</span>
                                    <span className="weekly-stat-label">{t('trainer.weekUpcoming')}</span>
                                </div>
                            </div>

                            {/* Mini Chart */}
                            {weeklyOverview?.dailyBreakdown && (
                                <div className="weekly-chart">
                                    <div className="chart-bars">
                                        {weeklyOverview.dailyBreakdown.map((day, index) => (
                                            <div key={index} className="chart-bar-wrapper">
                                                <div
                                                    className={`chart-bar ${day.isToday ? 'chart-bar--today' : ''}`}
                                                    style={{
                                                        height: `${Math.max((day.count / getMaxDailyCount()) * 100, 5)}%`
                                                    }}
                                                >
                                                    <span className="bar-value">{day.count}</span>
                                                </div>
                                                <span className="chart-label">{t(`trainer.dayNames.${day.dayName}`, { defaultValue: day.dayName })}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {weeklyOverview?.completionRate > 0 && (
                                <Widget.Progress
                                    value={weeklyOverview.completionRate}
                                    label={t('trainer.weekExecution')}
                                    variant={
                                        weeklyOverview.completionRate >= 75 ? 'success' :
                                            weeklyOverview.completionRate >= 50 ? 'primary' :
                                                weeklyOverview.completionRate >= 25 ? 'warning' : 'danger'
                                    }
                                />
                            )}
                        </Widget>
                    </WidgetGrid.Item>

                </WidgetGrid>
            </div>
        </TrainerLayout>
    );
};

export default TrainerDashboard;
